import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  BaseCommonTableComponent,
  ITemplateContext
} from '../../../shared/tables/common-table/base-common-table.component';
import {
  DateUtil,
  PaginationOptionsDto,
  PaginationResultsDto,
  ScheduledHeroEventListResponseDto
} from '@whetstoneeducation/hero-common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TableFilterOptions } from '../../../shared/tables/table-filters/table-filters';
import { TableFiltersPageKeyEnum } from '../../../shared/tables/table-filters/table-filters-page-key.enum';
import { AppEventService } from '../events.service';
import { ITableFilters } from '../../../shared/tables/table-filters/table-filters.interface';
import { Subject, takeUntil } from 'rxjs';
import { HeaderButtonAction } from '../../../shared/page-header/header-button';
import { AppPageHeaderService } from '../../../shared/page-header/page-header.service';

export enum HistoricalScheduledHeroEventListTableColumns {
  ID = 'Event Id',
  EVENT_NAME = 'Event Name',
  EVENT_CATEGORY = 'Event Category',
  EVENT_TIME = 'Event Time',
  RECURRENCE = 'Recurrence',
  DATE = 'Date of Event',
  ACTION = 'Action'
}

interface IActionsColumnContext {
  row: ScheduledHeroEventListResponseDto;
}

@Component({
  selector: 'app-historical-scheduled-hero-event-list',
  templateUrl: './historical-scheduled-hero-event-list.component.html',
  styleUrls: ['./historical-scheduled-hero-event-list.component.scss']
})
export class AppHistoricalScheduledHeroEventListComponent
  extends BaseCommonTableComponent<
    ScheduledHeroEventListResponseDto,
    HistoricalScheduledHeroEventListTableColumns
  >
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('actionsColumn')
  public actionsColumn: TemplateRef<ITemplateContext<IActionsColumnContext>>;
  public filters: PaginationOptionsDto;
  public columns;
  private destroy$: Subject<void> = new Subject<void>();

  public constructor(
    public eventsService: AppEventService,
    public route: ActivatedRoute,
    public router: Router,
    public dialog: MatDialog,
    public pageHeaderService: AppPageHeaderService
  ) {
    super();
    this.templates = {};
    this.columns = HistoricalScheduledHeroEventListTableColumns;
  }

  public ngOnInit() {
    this.loadData();
    this.pageHeaderService.buttonAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (action: HeaderButtonAction) => {
        switch (action) {
          case HeaderButtonAction.BACK:
            return this.router.navigate(['events']);
          default:
            return;
        }
      });
  }

  public ngAfterViewInit(): void {
    if (this.actionsColumn) {
      this.templates['actionsColumn'] = this.actionsColumn;
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadData(): void {
    this.isLoading = true;
    const data = this.route.snapshot.data
      .data as PaginationResultsDto<ScheduledHeroEventListResponseDto>;
    this.tableFilters = TableFilterOptions.getPageDefault(
      TableFiltersPageKeyEnum.HISTORICAL_SCHEDULED_HERO_EVENT_LIST
    );
    this.tableFilters.count = data.options.totalItems;
    this.showPagination = true;
    this.dataArr = data.results;
    this.isLoading = false;
  }

  public async updateTableFilters(
    newTableFilters: ITableFilters
  ): Promise<void> {
    this.tableFilters = newTableFilters;
    await this.applyFilters();
  }

  public async applyFilters(): Promise<void> {
    this.isLoading = true;
    const response =
      await this.eventsService.getHistoricalScheduledHeroEventsByHeroEventId(
        this.route.snapshot.params.id,
        new PaginationOptionsDto(this.tableFilters)
      );

    this.tableFilters.count = response.options.totalItems;
    this.dataArr = response.results;
    this.isLoading = false;
  }

  public async nav(commands: string[], options?: any) {
    await this.router.navigate(commands, options);
  }

  public getColumnValue(
    column: string,
    row: ScheduledHeroEventListResponseDto
  ): any {
    switch (column) {
      case HistoricalScheduledHeroEventListTableColumns.ID:
        return row.id;
      case HistoricalScheduledHeroEventListTableColumns.EVENT_NAME:
        return row.name;
      case HistoricalScheduledHeroEventListTableColumns.EVENT_CATEGORY:
        return row.category;
      case HistoricalScheduledHeroEventListTableColumns.EVENT_TIME:
        return DateUtil.convertToDisplayTime(new Date(row.startTime).getTime());
      case HistoricalScheduledHeroEventListTableColumns.RECURRENCE:
        return row.recurrence;
      case HistoricalScheduledHeroEventListTableColumns.DATE:
        return DateUtil.convertToDisplayDate(new Date(row.startDate).getTime());
      case HistoricalScheduledHeroEventListTableColumns.ACTION:
        return {
          context: {
            row
          },
          template: this.templates.actionsColumn
        };
      default:
        return '';
    }
  }
}
